import { useEffect, useState } from 'react';
import { api, silently, type ImproveRow } from './api';

/**
 * 「사용 설명서」 메뉴 배지 — **마지막으로 설명서를 연 뒤 반영된 개선요청 수**(2026-10-01 way 「업데이트할 때마다 볼 수 있게」).
 *
 * 배포마다 점을 띄우면 하루 몇 번씩 떠서 아무도 안 본다 — 그래서 «직원이 알아야 할 변화»(완료된 전체공개 요청)만 센다.
 * 기준 시각은 이 브라우저의 localStorage(기기별). **처음 여는 사람은 0 에서 시작**한다 — 지난 몇 달치가 한꺼번에 뜨면 배지가 의미를 잃는다.
 * 설명서 화면을 열면 지금 시각으로 옮기고, 같은 창의 메뉴 배지에 바로 알린다(`EVENT`).
 */
const KEY = 'businweb.manual.seen';
const EVENT = 'businweb:manual-seen';

const at = (r: ImproveRow) => r.last_reply_at ?? r.created_at;

export function readSeen(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** 설명서를 열 때 부른다. 돌려주는 값 = 그 직전 기준(화면이 「새」 표시를 그 뒤 항목에 붙인다). */
export function markManualSeen(): string | null {
  const prev = readSeen();
  try {
    localStorage.setItem(KEY, kstNow());
  } catch {
    /* 저장이 막힌 브라우저면 배지만 안 줄어든다 */
  }
  window.dispatchEvent(new Event(EVENT));
  return prev;
}

/** DB 시각과 같은 모양(KST 벽시계 `YYYY-MM-DD HH:MM:SS`) — 문자열 비교로 앞뒤를 가린다. */
function kstNow(): string {
  return new Date(Date.now() + 9 * 3_600_000).toISOString().slice(0, 19).replace('T', ' ');
}

export function isNewSince(r: ImproveRow, seen: string | null): boolean {
  return !!seen && at(r).replace('T', ' ').slice(0, 19) > seen;
}

export function useManualNews(): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    let off = false;
    if (readSeen() === null) {
      try {
        localStorage.setItem(KEY, kstNow());
      } catch {
        /* 무시 */
      }
    }
    const refresh = () =>
      silently(() => {
        api.listImprovements({ status: 'done', limit: 50 })
          .then((r) => {
            if (off) return;
            const seen = readSeen();
            setN(r.rows.filter((x) => x.visibility === 'all' && isNewSince(x, seen)).length);
          })
          .catch(() => { /* 배지는 없어도 그만 */ });
      });
    refresh();
    const timer = window.setInterval(refresh, 10 * 60_000);
    const onSeen = () => setN(0);
    window.addEventListener(EVENT, onSeen);
    return () => {
      off = true;
      window.clearInterval(timer);
      window.removeEventListener(EVENT, onSeen);
    };
  }, []);
  return n;
}
